import React, { useState } from 'react';
import { Tooltip, IconButton, CircularProgress } from '@mui/material';
import { TableChart as TableChartIcon } from '@mui/icons-material';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

// ─── Brand tokens ─────────────────────────────────────────────────────────────
const BRAND = {
  primary:        '08000a',
  primaryLight:   '2d2a6e',
  secondary:      'E8320A',
  secondaryLight: '5DCAA5',
  secondaryDark:  '0F6E56',
  tertiary:       'E8320A',
  neutralLight:   'F1EFE8',
  neutralDark:    '2C2C2A',
  white:          'FFFFFF',
  rowAlt:         'EBF6F1',
  rowBase:        'F8F7F4',
  borderLight:    'D5D3CC',
};

// ─── Style helpers ────────────────────────────────────────────────────────────
const headerFill   = { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${BRAND.primaryLight}` } };
const rowBaseFill  = { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${BRAND.rowBase}` } };
const rowAltFill   = { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${BRAND.rowAlt}` } };
const accentFill   = { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${BRAND.secondary}` } };
const tertiaryFill = { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${BRAND.tertiary}` } };
const titleFill    = { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${BRAND.primary}` } };

const borderThin = (color = BRAND.borderLight) => ({
  top:    { style: 'thin', color: { argb: `FF${color}` } },
  left:   { style: 'thin', color: { argb: `FF${color}` } },
  bottom: { style: 'thin', color: { argb: `FF${color}` } },
  right:  { style: 'thin', color: { argb: `FF${color}` } },
});

// ─── Main export function ─────────────────────────────────────────────────────
const exportToExcel = async (rawData, titulo, headersColumn) => {
  const visibleCols = headersColumn.filter(c => c.show !== false);

  const wb = new ExcelJS.Workbook();
  wb.creator  = 'OnAir';
  wb.created  = new Date();
  wb.modified = new Date();

  const ws = wb.addWorksheet('Datos', {
    pageSetup: { paperSize: 9, orientation: 'landscape', fitToPage: true, fitToWidth: 1 },
    views: [{ state: 'frozen', ySplit: 5 }], // freeze above data rows
  });

  const colCount = visibleCols.length;

  // ── 1. Title row ──────────────────────────────────────────────────────────
  ws.mergeCells(1, 1, 1, colCount);
  const titleCell = ws.getCell('A1');
  titleCell.value = 'ON-AIR';
  titleCell.font  = { bold: true, size: 18, color: { argb: `FF${BRAND.white}` }, name: 'Arial' };
  titleCell.fill  = titleFill;
  titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
  ws.getRow(1).height = 32;

  // ── 2. Subtitle row ───────────────────────────────────────────────────────
  ws.mergeCells(2, 1, 2, colCount);
  const subCell = ws.getCell('A2');
  subCell.value = titulo || 'Reporte';
  subCell.font  = { bold: true, size: 11, color: { argb: `FF${BRAND.secondaryLight}` }, name: 'Arial' };
  subCell.fill  = titleFill;
  subCell.alignment = { horizontal: 'center', vertical: 'middle' };
  ws.getRow(2).height = 20;

  // ── 3. Metadata row ───────────────────────────────────────────────────────
  ws.mergeCells(3, 1, 3, colCount);
  const now = new Date();
  const metaCell = ws.getCell('A3');
  metaCell.value = `Generado: ${now.toLocaleDateString('es-EC', { day:'2-digit', month:'long', year:'numeric' })}  ·  ${rawData.length} registro${rawData.length !== 1 ? 's' : ''}  ·  Confidencial`;
  metaCell.font  = { italic: true, size: 8, color: { argb: `FF${BRAND.neutralLight}` }, name: 'Arial' };
  metaCell.fill  = { type: 'pattern', pattern: 'solid', fgColor: { argb: `FF${BRAND.primaryLight}` } };
  metaCell.alignment = { horizontal: 'center', vertical: 'middle' };
  ws.getRow(3).height = 16;

  // ── 4. Accent strip row ───────────────────────────────────────────────────
  const accentSplit = Math.ceil(colCount * 0.75);
  for (let c = 1; c <= colCount; c++) {
    const cell = ws.getCell(4, c);
    cell.fill = c <= accentSplit ? accentFill : tertiaryFill;
  }
  ws.getRow(4).height = 4;

  // ── 5. Column headers ─────────────────────────────────────────────────────
  const headerRow = ws.getRow(5);
  headerRow.height = 22;
  visibleCols.forEach((col, i) => {
    const cell = headerRow.getCell(i + 1);
    cell.value = (col.label || col.id || '').toUpperCase();
    cell.font  = { bold: true, size: 8.5, color: { argb: `FF${BRAND.white}` }, name: 'Arial' };
    cell.fill  = headerFill;
    cell.alignment = { horizontal: col.align || 'left', vertical: 'middle', wrapText: false };
    cell.border = borderThin('FFFFFF');
  });

  // ── 6. Data rows ──────────────────────────────────────────────────────────
  rawData.forEach((row, rowIdx) => {
    const excelRow = ws.getRow(6 + rowIdx);
    excelRow.height = 18;

    visibleCols.forEach((col, colIdx) => {
      const cell  = excelRow.getCell(colIdx + 1);
      const value = row[col.id];
      cell.value  = value != null ? value : '';
      cell.font   = {
        bold: !!col.bolder,
        size: 8,
        name: 'Arial',
        color: { argb: `FF${BRAND.neutralDark}` },
      };
      cell.fill   = rowIdx % 2 === 0 ? rowBaseFill : rowAltFill;
      cell.alignment = { horizontal: col.align || 'left', vertical: 'middle' };
      cell.border = {
        bottom: { style: 'thin', color: { argb: rowIdx % 2 === 0 ? `FF${BRAND.borderLight}` : `FFD5EDE5` } },
      };
    });
  });

  // ── 7. Column widths ──────────────────────────────────────────────────────
  visibleCols.forEach((col, i) => {
    const wsCol = ws.getColumn(i + 1);
    // Try to derive a reasonable width from the col.width prop (usually px)
    const pxWidth = col.width ? parseInt(col.width) : 100;
    wsCol.width = Math.max(10, Math.min(40, Math.round(pxWidth / 7)));
  });

  // ── 8. Totals footer (numeric columns) ───────────────────────────────────
  const numericColIndexes = visibleCols
    .map((col, i) => ({ col, i }))
    .filter(({ col }) => rawData.some(r => typeof r[col.id] === 'number'));

  if (numericColIndexes.length > 0) {
    const totalRowIdx = 6 + rawData.length;
    const totalRow = ws.getRow(totalRowIdx);
    totalRow.height = 20;

    // Label
    const labelCell = totalRow.getCell(1);
    labelCell.value = 'TOTAL';
    labelCell.font  = { bold: true, size: 8.5, color: { argb: `FF${BRAND.white}` }, name: 'Arial' };
    labelCell.fill  = accentFill;
    labelCell.alignment = { horizontal: 'left', vertical: 'middle' };

    numericColIndexes.forEach(({ col, i }) => {
      const cell = totalRow.getCell(i + 1);
      const dataStart = 6;
      const dataEnd   = 5 + rawData.length;
      const colLetter = ws.getColumn(i + 1).letter;
      cell.value = { formula: `SUM(${colLetter}${dataStart}:${colLetter}${dataEnd})` };
      cell.font  = { bold: true, size: 8.5, color: { argb: `FF${BRAND.white}` }, name: 'Arial' };
      cell.fill  = accentFill;
      cell.numFmt = '#,##0.00';
      cell.alignment = { horizontal: col.align || 'right', vertical: 'middle' };
    });

    // Fill non-numeric cells in totals row
    for (let c = 1; c <= colCount; c++) {
      const cell = totalRow.getCell(c);
      if (!cell.value) {
        cell.fill = accentFill;
      }
    }
  }

  // ── 9. Write & download ───────────────────────────────────────────────────
  const buffer = await wb.xlsx.writeBuffer();
  const blob   = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  saveAs(blob, `${titulo || 'reporte'}.xlsx`);
};

// ─── React Component ──────────────────────────────────────────────────────────
export const ExcelComponent = ({ data, titulo, headersColumn, isSmall }) => {
  const [loading, setLoading] = useState(false);

  const handleExport = async () => {
    if (loading) return;
    setLoading(true);
    try {
      await exportToExcel(data, titulo, headersColumn);
    } catch (err) {
      console.error('[ExcelComponent]', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Tooltip title="Exportar Excel" placement="top" arrow>
      <span>
        <IconButton
          onClick={handleExport}
          disabled={loading}
          size="small"
          sx={{
            backgroundColor: '#F1EFE8',
            color: '#0e4022',
            border: '1px solid #E8320A22',
            borderRadius: '8px',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: '#1e6c49',
              color: '#fff',
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 12px #E8320A44',
            },
            '&.Mui-disabled': {
              backgroundColor: '#F1EFE8',
              opacity: 0.6,
            },
          }}
        >
          {loading
            ? <CircularProgress size={16} thickness={5} sx={{ color: '#E8320A' }} />
            : <TableChartIcon fontSize="small" />
          }
        </IconButton>
      </span>
    </Tooltip>
  );
};